<template>

  <div>
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title">
              Editing {{ agent.code }} - {{ agent.name }}
            </h1>
            <p class="log_info">
              Created by {{ agent.createdByNvs ? agent.createdByNvs.name : '-' }} on {{ agent.createdAt ? dateFormatWithTime(agent.createdAt) : '-' }}<br>Last updated on {{ agent.updatedAt ? dateFormatWithTime(agent.updatedAt) : '-' }}
            </p>
          </b-col>
        </b-row>

        <b-alert
          v-if="pendingSalesOrders.length"
          variant="danger"
          dismissible
          show
          class="mb-1"
        >
          <div class="alert-body">
            <p
              v-for="so in pendingSalesOrders"
              :key="so.stringID"
              class="mb-0"
            >
              <strong>Important:</strong> Editing this agent's profile will also update the Sales Agent for {{ so.stringID }} — {{ so.customerName || 'N/A' }}.
            </p>
          </div>
        </b-alert>
      </b-col>
    </b-row>

    <b-form @submit.prevent="submitForm">
      <validation-observer ref="probationaryAgentEditForm">
        <b-card header-tag="header">
          <template #header>
            <h3 class="align-middle mr-auto">
              Profile Details
            </h3>
          </template>
          <b-row>
            <b-col cols="md-12">
              <b-media class="mb-2 agent__media">
                <template #aside>
                  <b-avatar
                    ref="previewEl"
                    :src="avatarSrc"
                    size="120"
                  />
                </template>
                <div class="d-flex flex-wrap mb-1">
                  <b-button
                    variant="primary"
                    @click="$refs.refInputEl.$el.childNodes[0].click()"
                  >
                    <b-form-file
                      ref="refInputEl"
                      v-model="avatar"
                      type="file"
                      class="d-none"
                      accept="image/*"
                      @input="setImage"
                    />
                    <span class="d-none d-sm-inline">Update</span>
                    <feather-icon
                      icon="EditIcon"
                      class="d-inline d-sm-none"
                    />
                  </b-button>
                  <b-button
                    variant="outline-primary"
                    class="ml-1"
                    @click="avatarSrc = null; avatar = null"
                  >
                    <span class="d-none d-sm-inline">Reset</span>
                    <feather-icon
                      icon="TrashIcon"
                      class="d-inline d-sm-none"
                    />
                  </b-button>
                </div>
                <p>Allowed JPEG, GIF or PNG. Max size of 800kB</p>
              </b-media>
            </b-col>
            <b-col cols="md-6">
              <b-form-group
                label="Agent Name*"
                label-for="h-name"
                label-cols-md="4"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Name"
                  vid="name"
                  rules="required"
                >
                  <b-form-input
                    id="h-name"
                    v-model="name"
                    placeholder="Agent Name"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Card Display Name"
                label-for="h-display-name"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-display-name"
                  v-model="displayName"
                  placeholder="Card Display Name"
                />
              </b-form-group>
              <b-form-group
                label="Agent Code"
                label-for="h-code"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-code"
                  :value="agent.code"
                  readonly
                />
              </b-form-group>
              <b-form-group
                label="Agency"
                label-for="h-agency"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-agency"
                  :value="agencyDisplay"
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="md-6">
              <b-form-group
                label="Join Date"
                label-for="h-join-date"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-join-date"
                  :value="agent.joinDate ? dateFormat(agent.joinDate) : ''"
                  readonly
                />
              </b-form-group>
              <b-form-group
                label="Confirm Date"
                label-for="h-confirm-date"
                label-cols-md="4"
              >
                <b-input-group>
                  <flat-pickr
                    id="h-confirm-date"
                    v-model="confirmDate"
                    class="form-control flat-pickr-group"
                    placeholder="Confirm Date"
                    :config="flatPickrConfig"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      class="cursor-pointer"
                      data-toggle
                      size="18"
                    />
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
              <b-form-group
                label="Expiry Date"
                label-for="h-exp-date"
                label-cols-md="4"
              >
                <b-input-group>
                  <flat-pickr
                    id="h-exp-date"
                    v-model="expDate"
                    class="form-control flat-pickr-group"
                    placeholder="Expiry Date"
                    :config="flatPickrConfig"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      class="cursor-pointer"
                      data-toggle
                      size="18"
                    />
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
              <b-form-group
                label="Status"
                label-for="h-status"
                label-cols-md="4"
              >
                <v-select
                  id="h-status"
                  v-model="status"
                  label="title"
                  :options="statusOptions"
                  :reduce="title => title.code"
                  :clearable="false"
                />
              </b-form-group>
              <b-form-group
                label="Remarks"
                label-for="h-remark"
                label-cols-md="4"
              >
                <b-form-textarea
                  id="h-remark"
                  v-model="remark"
                  placeholder="Remarks"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-card header-tag="header">
          <template #header>
            <h3 class="align-middle mr-auto">
              Personal Information
            </h3>
          </template>
          <b-row>
            <b-col cols="md-6">
              <b-form-group
                label="Sex"
                label-for="h-sex"
                label-cols-md="4"
              >
                <v-select
                  id="h-sex"
                  v-model="sex"
                  label="title"
                  :options="genderOptions"
                  :reduce="title => title.code"
                  :clearable="false"
                />
              </b-form-group>
              <b-form-group
                label="Race"
                label-for="h-race"
                label-cols-md="4"
              >
                <v-select
                  id="h-race"
                  v-model="race"
                  label="title"
                  :options="raceOptions"
                  :reduce="title => title.code"
                  :clearable="false"
                />
              </b-form-group>
            </b-col>
            <b-col cols="md-6">
              <b-form-group
                label="Marital"
                label-for="h-marital"
                label-cols-md="4"
              >
                <v-select
                  id="h-marital"
                  v-model="maritalStatus"
                  label="title"
                  :options="maritalOptions"
                  :reduce="title => title.code"
                  :clearable="false"
                />
              </b-form-group>
              <b-form-group
                label="Date of Birth"
                label-for="h-dob"
                label-cols-md="4"
              >
                <b-input-group>
                  <flat-pickr
                    id="h-dob"
                    v-model="dob"
                    class="form-control flat-pickr-group"
                    placeholder="Date of Birth"
                    :config="flatPickrConfig"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      class="cursor-pointer"
                      data-toggle
                      size="18"
                    />
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-card header-tag="header">
          <template #header>
            <h3 class="align-middle mr-auto">
              Correspondence Contact
            </h3>
          </template>
          <b-row>
            <b-col cols="md-6">
              <b-form-group
                label="Primary Contact"
                label-for="h-contact"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-contact"
                  v-model="contact"
                  placeholder="Primary Contact"
                />
              </b-form-group>
              <b-form-group
                label="Secondary Contact"
                label-for="h-home-contact"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-home-contact"
                  v-model="homeContact"
                  placeholder="Secondary Contact"
                />
              </b-form-group>
              <b-form-group
                label="Email*"
                label-for="h-email"
                label-cols-md="4"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  vid="email"
                  rules="required|email"
                >
                  <b-form-input
                    id="h-email"
                    v-model="email"
                    placeholder="Email"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="md-6">
              <b-form-group
                label="Address 1"
                label-for="h-address1"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-address1"
                  v-model="address1"
                  placeholder="Address 1"
                />
              </b-form-group>
              <b-form-group
                label="Address 2"
                label-for="h-address2"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-address2"
                  v-model="address2"
                  placeholder="Address 2"
                />
              </b-form-group>
              <b-form-group
                label="Post Code"
                label-for="h-post-code"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-post-code"
                  v-model="postCode"
                  placeholder="Post Code"
                />
              </b-form-group>
              <b-form-group
                label="Country Code"
                label-for="h-country-code"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-country-code"
                  v-model="country"
                  placeholder="Country Code"
                />
              </b-form-group>
              <b-form-group
                label="State Code"
                label-for="h-state-code"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-state-code"
                  v-model="state"
                  placeholder="State Code"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-card header-tag="header">
          <template #header>
            <h3 class="align-middle mr-auto">
              Emergency Contact
            </h3>
          </template>
          <b-row>
            <b-col cols="md-6">
              <b-form-group
                label="Contact Person"
                label-for="h-ec-name"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-ec-name"
                  v-model="emergencyContactName"
                  placeholder="Emergency Contact Person"
                />
              </b-form-group>
              <b-form-group
                label="Mobile No."
                label-for="h-ec-mobile"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-ec-mobile"
                  v-model="emergencyContactMobile"
                  placeholder="Mobile No."
                />
              </b-form-group>
            </b-col>
            <b-col cols="md-6">
              <b-form-group
                label="Relationship"
                label-for="h-ec-relation"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-ec-relation"
                  v-model="emergencyContactRelation"
                  placeholder="Relationship"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-card header-tag="header">
          <template #header>
            <h3 class="align-middle mr-auto">
              Bank Details
            </h3>
          </template>
          <b-row>
            <b-col cols="md-6">
              <b-form-group
                label="Bank Name"
                label-for="h-bank-name"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-bank-name"
                  v-model="bankName"
                  placeholder="Bank Name"
                />
              </b-form-group>
              <b-form-group
                label="Bank Code"
                label-for="h-bank-code"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-bank-code"
                  v-model="bankCode"
                  placeholder="Bank Code"
                />
              </b-form-group>
            </b-col>
            <b-col cols="md-6">
              <b-form-group
                label="Bank Branch"
                label-for="h-bank-branch"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-bank-branch"
                  v-model="bankBranch"
                  placeholder="Bank Branch"
                />
              </b-form-group>
              <b-form-group
                label="Bank Account No."
                label-for="h-bank-account-no"
                label-cols-md="4"
              >
                <b-form-input
                  id="h-bank-account-no"
                  v-model="bankAccountNo"
                  placeholder="Bank Account No."
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-card header-tag="header">
          <template #header>
            <h3 class="align-middle mr-auto">
              Supporting Document(s)
            </h3>
          </template>
          <b-row>
            <b-col cols="12">
              <vue-dropzone
                id="probationary-agent-attachments"
                ref="myVueDropzone"
                disabled
                :options="dropzoneOptions"
                :use-custom-slot="true"
                @vdropzone-files-added="processFile"
                @vdropzone-removed-file="fileRemoved"
                @vdropzone-error="handleError"
              >
                <div class="dropzone-custom-content">
                  <h3 class="dropzone-custom-title">
                    Drop file here or click to upload
                  </h3>
                  <div class="subtitle">
                    Images should not bigger than 5 mb. JPEG or PDF only.
                  </div>
                  <b-button
                    type="button"
                    variant="primary"
                    class="mt-2"
                  >
                    Upload file
                  </b-button>
                </div>
              </vue-dropzone>
            </b-col>
          </b-row>
        </b-card>

        <b-nav class="wrap-border save-nav">
          <li class="nav-item ml-auto">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              :to="{ name: 'agency-agents-probationary-show', params: { id: $route.params.id } }"
            >
              Cancel
            </b-button>
          </li>
          <li class="nav-item mr-1">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              <span class="align-middle">Save Changes</span>
            </b-button>
          </li>
        </b-nav>
      </validation-observer>
    </b-form>
  </div>

</template>

<script>
import {
  BCard, BRow, BCol, BForm, BFormGroup, BFormInput, BFormTextarea, BButton, BInputGroup, BInputGroupAppend,
  BMedia, BAvatar, BFormFile, BNav, BAlert,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import '@core/scss/vue/libs/vue-flatpicker.scss'
// eslint-disable-next-line import/no-extraneous-dependencies
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import Ripple from 'vue-ripple-directive'
import store from '@/store/index'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BMedia,
    BAvatar,
    BFormFile,
    BNav,
    BAlert,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    flatPickr,
    vueDropzone: vue2Dropzone,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
      agent: {},
      pendingSalesOrders: [],
      name: '',
      displayName: '',
      email: '',
      contact: '',
      homeContact: '',
      sex: '',
      race: '',
      maritalStatus: '',
      dob: '',
      status: '',
      remark: '',
      address1: '',
      address2: '',
      postCode: '',
      country: '',
      state: '',
      emergencyContactName: '',
      emergencyContactMobile: '',
      emergencyContactRelation: '',
      bankName: '',
      bankCode: '',
      bankBranch: '',
      bankAccountNo: '',
      confirmDate: '',
      expDate: '',
      genderOptions: [
        { title: 'MALE', code: 'MALE' },
        { title: 'FEMALE', code: 'FEMALE' },
        { title: 'N/A', code: 'N/A' },
      ],
      raceOptions: [
        { title: 'CHINESE', code: 'CHINESE' },
        { title: 'INDIAN', code: 'INDIAN' },
        { title: 'N/A', code: 'N/A' },
        { title: 'OTHERS', code: 'OTHERS' },
      ],
      maritalOptions: [
        { title: 'DIVORCED', code: 'DIVORCED' },
        { title: 'MARRIED', code: 'MARRIED' },
        { title: 'OTHERS', code: 'OTHERS' },
        { title: 'SINGLE', code: 'SINGLE' },
      ],
      statusOptions: [
        { title: 'Active', code: 'Active' },
        { title: 'Inactive', code: 'Inactive' },
        { title: 'Expired', code: 'Expired' },
      ],
      flatPickrConfig: {
        wrap: true, enableTime: false, dateFormat: 'd/m/Y', disableMobile: true, static: true,
      },
      avatar: null,
      avatarSrc: '',
      imageName: '',
      attachments: [],
      dropzoneOptions: {
        autoProcessQueue: false,
        url: 'https://httpbin.org/post',
        acceptedFiles: 'image/jpeg,image/png,application/pdf',
        maxFilesize: 5,
        addRemoveLinks: true,
        dictFileTooBig: 'File is too big ({{filesize}}mb). Max filesize: {{maxFilesize}}mb.',
      },
    }
  },
  computed: {
    agencyDisplay() {
      if (!this.agent.agent) return ''
      return `${this.agent.agent.agencyName || '-'} (${this.agent.agent.agencyCode || '-'})`
    },
  },
  mounted() {
    window.scrollTo(0, 0)
    this.fetchAgentDetail()
  },
  methods: {
    fetchAgentDetail() {
      this.$http.get(`customer/agents/probationary/${this.$route.params.id}/show`)
        .then(response => {
          this.agent = response.data.agent
          this.pendingSalesOrders = response.data.pendingSalesOrders || []
          this.name = this.agent.name || ''
          this.displayName = this.agent.displayName || ''
          this.email = this.agent.email || ''
          this.contact = this.agent.contact || ''
          this.homeContact = this.agent.homeContact || ''
          this.sex = this.agent.sex || ''
          this.race = this.agent.race || ''
          this.maritalStatus = this.agent.maritalStatus || ''
          this.dob = this.agent.dob ? this.dateFormat(this.agent.dob) : ''
          this.status = this.agent.status || ''
          this.remark = this.agent.remark || ''
          this.address1 = this.agent.address1 || ''
          this.address2 = this.agent.address2 || ''
          this.postCode = this.agent.postCode || ''
          this.country = this.agent.country || ''
          this.state = this.agent.state || ''
          this.emergencyContactName = this.agent.emergencyContactName || ''
          this.emergencyContactMobile = this.agent.emergencyContactMobile || ''
          this.emergencyContactRelation = this.agent.emergencyContactRelation || ''
          this.bankName = this.agent.bankName || ''
          this.bankCode = this.agent.bankCode || ''
          this.bankBranch = this.agent.bankBranch || ''
          this.bankAccountNo = this.agent.bankAccountNo || ''
          this.confirmDate = this.agent.confirmDate ? this.dateFormat(this.agent.confirmDate) : ''
          this.expDate = this.agent.expDate ? this.dateFormat(this.agent.expDate) : ''
          this.avatarSrc = this.agent.image
          if (this.agent.image) {
            this.imageName = this.agent.image.split('/').pop()
          }

          const existingAttachments = response.data.attachments || []
          existingAttachments.forEach(arrayItem => {
            const fileName = arrayItem.data
            const file = {
              size: (arrayItem.size * 1024 * 1024), name: fileName, type: arrayItem.type, description: arrayItem.description,
            }
            this.$refs.myVueDropzone.manuallyAddFile(file, arrayItem.path)
            this.attachments.push(arrayItem)
          })
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    setImage() {
      if (this.avatar && this.avatar.size <= 800000) {
        const reader = new FileReader()
        reader.readAsDataURL(this.avatar)
        reader.onload = () => {
          this.avatarSrc = reader.result
        }
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please select a smaller image',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    processFile(file) {
      const acceptedTypes = ['image/jpeg', 'image/png', 'application/pdf']
      file.forEach(element => {
        if (acceptedTypes.includes(element.type)) {
          if (element.size <= 5 * 1024 * 1024) {
            const exist = this.attachments.find(o => o.name === element.name && o.size === (element.size / (1024 * 1024)).toFixed(2))
            if (exist) {
              setTimeout(() => {
                if (element.previewElement) {
                  element.previewElement.remove()
                }
              }, 500)
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Duplicate File',
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              })
            } else {
              const fileObj = {}
              fileObj.name = element.name
              fileObj.data = element
              fileObj.size = (element.size / (1024 * 1024)).toFixed(2)
              fileObj.type = element.type
              fileObj.description = ''
              fileObj.storage = 'local'
              this.attachments.push(fileObj)
            }
          }
        }
      })
    },
    fileRemoved(file) {
      if (file.manuallyAdded) {
        this.attachments = this.attachments.filter(element => element.data !== file.name)
      } else {
        this.attachments = this.attachments.filter(element => element.name !== file.name)
      }

      const previewLength = document.getElementsByClassName('dz-preview').length
      if (!previewLength) {
        this.$refs.myVueDropzone.removeAllFiles()
      }
    },
    handleError(file, message) {
      this.$refs.myVueDropzone.removeFile(file)
      if (message !== 'Upload canceled.') {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async submitForm() {
      const success = await this.$refs.probationaryAgentEditForm.validate()
      if (!success) return

      store.commit('appConfig/UPDATE_CONTINUOUS_LOADER', true)
      const formData = new FormData()
      let shouldUpload = false
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.attachments.length; i++) {
        if (this.attachments[i].data instanceof File) {
          // eslint-disable-next-line no-await-in-loop
          const res = await this.uploadFileToAws(this.attachments[i].data)
          const { fileUrl, fileName, upload } = res
          if (upload && fileUrl) {
            this.attachments[i].data = fileName
            this.attachments[i].storage = 'aws'
            formData.append('sAttachments', JSON.stringify(this.attachments[i]))
          } else {
            shouldUpload = (upload && fileUrl)
            formData.append('attachments', this.attachments[i].data)
          }
        } else {
          formData.append('sAttachments', JSON.stringify(this.attachments[i]))
        }
      }

      if (this.avatar) {
        const res = await this.uploadFileToAws(this.avatar)
        const { fileUrl, fileName, upload } = res
        if (upload && fileUrl) {
          formData.append('image', fileName)
          formData.append('imageStorage', 'aws')
        } else {
          formData.append('profileImage', this.avatar)
        }
      } else {
        formData.append('imageStorage', this.agent.imageStorage || 'local')
        formData.append('image', this.imageName)
      }

      formData.append('shouldUpload', shouldUpload)
      store.commit('appConfig/UPDATE_CONTINUOUS_LOADER', false)

      formData.append('name', this.name)
      formData.append('displayName', this.displayName)
      formData.append('email', this.email)
      formData.append('contact', this.contact)
      formData.append('homeContact', this.homeContact)
      formData.append('sex', this.sex)
      formData.append('race', this.race)
      formData.append('maritalStatus', this.maritalStatus)
      formData.append('dob', this.dob)
      formData.append('status', this.status)
      formData.append('remark', this.remark)
      formData.append('address1', this.address1)
      formData.append('address2', this.address2)
      formData.append('postCode', this.postCode)
      formData.append('country', this.country)
      formData.append('state', this.state)
      formData.append('emergencyContactName', this.emergencyContactName)
      formData.append('emergencyContactMobile', this.emergencyContactMobile)
      formData.append('emergencyContactRelation', this.emergencyContactRelation)
      formData.append('bankName', this.bankName)
      formData.append('bankCode', this.bankCode)
      formData.append('bankBranch', this.bankBranch)
      formData.append('bankAccountNo', this.bankAccountNo)
      formData.append('confirmDate', this.confirmDate)
      formData.append('expDate', this.expDate)

      this.$http.patch(`customer/agents/probationary/${this.$route.params.id}/update`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(response => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Probationary Agent updated successfully.',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$router.push({ name: 'agency-agents-probationary-show', params: { id: this.$route.params.id } })
        })
        .catch(error => {
          const message = (error.data && error.data.errors && error.data.errors[0] && error.data.errors[0].msg)
            || (error.data && error.data.message)
            || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE
          this.$toast({
            component: ToastificationContent,
            props: {
              title: message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
  },
}
</script>

<style scoped>
  .dropzone-custom-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }
  .dropzone-custom-title {
    margin-top: 0;
    color: #104D9D;
  }
  .subtitle {
    color: #333333;
  }
  .vs__dropdown-option--disabled {
    background: #ededed;
    color: #000000;
  }
  .agent__media .media-body{
    display: flex;
    flex-direction: column;
    align-self: center;
  }
</style>
